import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar as faCalendarRegular } from '@fortawesome/free-regular-svg-icons';
import { isWastageDateDisabled, parseYmd, toYmd } from '../utils/wastageDateBounds';
import CalendarSelect from './CalendarSelect';

/**
 * The wastage date picker — the same control Stock-In has, on the Wastage screen.
 *
 * ─── HOW "THE SAME CSS AS STOCK-IN" IS ACHIEVED ────────────────────────────
 *
 * Not by copying rules. Every class this component emits is styled by GLOBAL, unscoped
 * CSS that is already in the bundle on every screen — `.date-picker-overlay`,
 * `.date-picker-popup`, `.calendar-header`, `.calendar-weekdays`, `.weekday`,
 * `.calendar-grid`, `.calendar-day` from VendorManagement.css, `.calendar-day.disabled`
 * from stockrepo.css, and the `.calendar-select-*` family from both StockInForm-module.css
 * and VendorManagement.css. App.js uses no React.lazy, so CRA emits one stylesheet and
 * all of it applies here.
 *
 * Several of those rules collide on `!important` and resolve by bundle order rather than
 * by intent. That is exactly WHY this file emits identical markup instead of hand-rolled
 * equivalents: whatever the cascade resolves to, it resolves the same way for this
 * calendar as for Stock-In's, and it keeps doing so if anyone edits either stylesheet.
 * A hand-rolled <select> here would look right today and drift the first time those
 * files were touched.
 *
 * Two rules that read like they matter and do not:
 *   · `.stockin-form .date-picker-popup` — there is no `.stockin-form` element anywhere
 *     in StockInForm.js, so those overrides are dead for Stock-In too.
 *   · `.calendar-month-select` / `.calendar-year-select` — no component emits them.
 *
 * The only deliberate additions over Stock-In's calendar are the selected-day highlight
 * (black on white, matching this screen's category pills) and the closed-through note,
 * which the reconciled-period rule needs somewhere to explain itself.
 */

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const getDaysInMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
const getFirstDayOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1).getDay();

/** 'YYYY-MM-DD' -> 'DD-MM-YYYY', the format the Stock-In field shows. */
function displayDate(ymd) {
  if (!ymd) return '';
  const parts = String(ymd).split('T')[0].split('-');
  if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return String(ymd);
}

const WastageDatePicker = ({
  value,
  onChange,
  reconciledThrough = null,
  // Optional extra narrowing, 'YYYY-MM-DD' — e.g. a banquet's wastage window
  // (event day … min(event + 3, today)). Absent, the picker behaves exactly as
  // before: today is the ceiling, the watermark the floor.
  minDate = null,
  maxDate = null,
  disabled = false,
  disabledHint = null,
  label = 'Wastage Date',
  style = {},
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  // The month the popup is showing. Seeded from `value` each time it opens, so
  // reopening after a change lands on the chosen month rather than the last browsed one.
  const [viewDate, setViewDate] = useState(() => parseYmd(value) || new Date());

  const openPicker = () => {
    if (disabled) return;
    setViewDate(parseYmd(value) || new Date());
    setOpen(true);
  };

  const navigateMonth = (direction) => {
    const d = new Date(viewDate);
    d.setDate(1); // guard: stepping months from the 31st skips a short month
    d.setMonth(d.getMonth() + direction);
    setViewDate(d);
  };

  const hasWindow = Boolean(minDate || maxDate);
  const windowLabel = [
    minDate ? displayDate(minDate) : null,
    maxDate ? displayDate(maxDate) : null,
  ].filter(Boolean).join(' to ');

  const handleDayClick = (day) => {
    const selected = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    onChange(toYmd(selected));
    setOpen(false);
  };

  const renderDays = () => {
    const daysInMonth = getDaysInMonth(viewDate);
    const firstDay = getFirstDayOfMonth(viewDate);
    const cells = [];

    for (let i = 0; i < firstDay; i += 1) {
      cells.push(<div key={`empty-${i}`} className="calendar-day empty" />);
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const cellDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
      const isDisabled = isWastageDateDisabled(cellDate, { reconciledThrough, minDate, maxDate });
      const isSelected = toYmd(cellDate) === value;
      const blockedByCount =
        isDisabled && reconciledThrough && toYmd(cellDate) <= reconciledThrough;
      // Blocked ONLY by the min/max window — open under the ordinary rule.
      const blockedByWindow =
        isDisabled && hasWindow && !blockedByCount
        && !isWastageDateDisabled(cellDate, { reconciledThrough });

      cells.push(
        <div
          key={day}
          className={`calendar-day${isDisabled ? ' disabled' : ''}`}
          // A disabled cell gets no handler at all — the same contract stock-in's
          // calendar uses, so `.calendar-day.disabled { pointer-events: none }` and the
          // absent handler agree instead of one masking a bug in the other.
          onClick={!isDisabled ? () => handleDayClick(day) : undefined}
          style={isSelected && !isDisabled ? { background: '#000', color: '#fff' } : undefined}
          title={blockedByCount
            ? `Closing stock has been submitted through ${displayDate(reconciledThrough)}`
            : blockedByWindow
              ? `Outside the allowed dates (${windowLabel})`
              : undefined}
        >
          {day}
        </div>
      );
    }
    return cells;
  };

  const years = Array.from({ length: 21 }, (_, k) => new Date().getFullYear() - 10 + k);

  return (
    <>
      <div
        className={`wastage-date-picker-trigger ${className}`.trim()}
        style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: '#ffffff',
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          padding: '0.9rem 1rem',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          justifyContent: 'space-between',
          width: '100%',
          boxSizing: 'border-box',
          outline: 'none',
          transition: 'none',
          position: 'relative',
          ...style,
        }}
        onClick={openPicker}
        title={disabled ? (disabledHint || 'Date is fixed') : 'Select the wastage date'}
      >
        <div style={{ display: 'flex', alignItems: 'center', flex: 1, textAlign: 'left', minWidth: 0, fontSize: '16px', color: '#000' }}>
          <FontAwesomeIcon
            icon={faCalendarRegular}
            style={{
              fontSize: '18px',
              color: '#6b7280',
              marginRight: '10px',
              flexShrink: 0,
            }}
          />
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <span style={{ fontWeight: '600' }}>{label.endsWith(':') ? label : `${label}:`}</span>{' '}
            <span>{displayDate(value)}</span>
          </div>
        </div>

        <input
          type="text"
          readOnly
          value={displayDate(value)}
          aria-label="Wastage date"
          style={{
            position: 'absolute',
            opacity: 0,
            pointerEvents: 'none',
            width: 0,
            height: 0,
          }}
          tabIndex={-1}
        />

        <svg 
          style={{
            width: '20px',
            height: '20px',
            color: '#6b7280',
            flexShrink: 0,
          }}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {open && (
        <div className="date-picker-overlay" onClick={() => setOpen(false)}>
          <div
            className="date-picker-popup"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Structurally identical to StockInForm.js's calendar header: the same
                two buttons, the same flex wrapper with gap 8, and the same
                CalendarSelect widths (130 for the month, 90 for the year). */}
            <div className="calendar-header">
              <button onClick={() => navigateMonth(-1)}>‹</button>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <CalendarSelect
                  options={MONTH_NAMES.map((m, i) => ({ label: m, value: i }))}
                  value={viewDate.getMonth()}
                  onChange={(val) => {
                    const d = new Date(viewDate);
                    // Guard Stock-In does not have: setMonth() on the 31st skips a
                    // short month (31 Mar -> 1 May). Anchoring to the 1st first means
                    // picking "February" from a 31-day month lands in February.
                    d.setDate(1);
                    d.setMonth(val);
                    setViewDate(d);
                  }}
                  width={130}
                />
                <CalendarSelect
                  options={years.map((y) => ({ label: String(y), value: y }))}
                  value={viewDate.getFullYear()}
                  onChange={(val) => {
                    const d = new Date(viewDate);
                    d.setDate(1); // same guard, for 29 Feb in a leap year
                    d.setFullYear(val);
                    setViewDate(d);
                  }}
                  width={90}
                />
              </div>
              <button onClick={() => navigateMonth(1)}>›</button>
            </div>

            <div className="calendar-weekdays">
              {WEEKDAYS.map((d) => <div key={d} className="weekday">{d}</div>)}
            </div>

            <div className="calendar-grid">{renderDays()}</div>

            {reconciledThrough && (
              <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
                Closed through {displayDate(reconciledThrough)} — earlier days cannot be edited.
              </div>
            )}

            {hasWindow && (
              <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
                Only {windowLabel} can be picked.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default WastageDatePicker;
